<script lang="ts">
	import { get } from 'svelte/store';
	import { convert } from 'src/helpers/utilities';
	import { consumption_by_device_account } from 'src/stores/features/consumption';

	import EnergyCircle from './EnergyCircle.svelte';
	import EnergyCircleCurve from './EnergyCurve.svelte';
	import EnergyLine from './EnergyLine.svelte';

	export let width = 0,
		height = 0,
		radius = 40,
		strokeWidth = 5,
		margin = 22,
		showBattery = true;

	width = width || radius * 16 + strokeWidth * 4;
	height = height || radius * 8 + strokeWidth * 4;

	const secondRowY = height / 2 - radius,
		heightSVG = height + margin * 2 + 10,
		unit = 'W';

	let solar = 0,
		home = 0,
		grid = 0,
		battery = 0;

	$: if ($consumption_by_device_account) {
		let solarPower = 0,
			homePower = 0;
		const consumptionDevice = get(consumption_by_device_account);
		for (const dev in consumptionDevice) {
			const inPower = consumptionDevice[dev]?.in_power || 0;
			if (inPower < 0) {
				solarPower += inPower;
			} else {
				homePower += inPower;
			}
		}
		solar = -solarPower;
		home = homePower;
		grid = Math.max(home - solar, 0);
		battery = Math.max(solar - home, 0);
	}
</script>

<div class="energy-distribution-card">
	<svg
		id="circle"
		width="100%"
		height="100%"
		style="max-width: 840px;"
		viewBox="0 0 {width} {heightSVG}"
		filter="url(#inset-shadow)"
	>
		<filter id="inset-shadow" x="-50%" y="-50%" width="200%" height="200%">
			<feComponentTransfer in="SourceAlpha">
				<feFuncA type="table" tableValues="1 0" />
			</feComponentTransfer>
			<feGaussianBlur stdDeviation="1" />
			<feOffset dx="0" dy="1" result="offsetblur" />
			<feComposite in2="offsetblur" operator="in" />
			<feComposite in2="SourceAlpha" operator="in" />
			<feMerge>
				<feMergeNode in="SourceGraphic" />
				<feMergeNode />
			</feMerge>
		</filter>
		<EnergyLine
			id="grid-to-home"
			y={secondRowY}
			x1={radius * 2 + strokeWidth / 2}
			y1={radius + strokeWidth + margin}
			x2={width - radius * 2 - strokeWidth / 2}
			y2={radius + strokeWidth + margin}
			{strokeWidth}
			play={solar < home}
		/>
		{#if showBattery}
			<EnergyLine
				id="solar-to-battery"
				y={radius * 2 + margin}
				x1={width / 2 + strokeWidth / 2}
				y1={0}
				x2={width / 2 + strokeWidth / 2}
				y2={height - radius * 4 + margin / 2}
				{strokeWidth}
				color="warning"
				play={solar - home > 0}
			/>
			<EnergyCircleCurve
				id="battery-to-home"
				x={-radius * 3 + 44}
				y={secondRowY - 28}
				position="lb"
				width={204}
				height={radius + 15}
				{strokeWidth}
				color="success"
				play={solar == 0 && home > 0 && battery > 0}
			/>
		{/if}
		<EnergyCircleCurve
			id="solar-to-grid"
			x={radius * 2 + strokeWidth + 25}
			y={secondRowY - 28}
			width={208}
			height={radius + 15}
			{strokeWidth}
			color="warning"
			play={!showBattery && solar - home > 0}
		/>
		<EnergyCircleCurve
			id="solar-to-home"
			x={-radius * 3 + 46}
			y={secondRowY - 28}
			position="lt"
			width={203}
			height={radius + 15}
			{strokeWidth}
			color="warning"
			play={solar > 0 && home > 0}
		/>
		<EnergyCircle
			type="solar"
			x={width / 2 - radius}
			y={0}
			{margin}
			value={convert(solar, unit).replace(' ', '')}
			titleTop={true}
			{radius}
			{strokeWidth}
		/>
		<EnergyCircle
			type="grid"
			y={secondRowY}
			{margin}
			value={convert(grid, unit).replace(' ', '')}
			{radius}
			{strokeWidth}
		/>
		<EnergyCircle
			type="home"
			x={width - radius * 2 - strokeWidth}
			y={secondRowY}
			{margin}
			value={convert(home, unit).replace(' ', '')}
			{radius}
			{strokeWidth}
		/>
		{#if showBattery}
			<EnergyCircle
				type="battery"
				x={width / 2 - radius}
				y={height - radius * 2 - strokeWidth + margin / 2}
				{margin}
				value={convert(battery, unit).replace(' ', '')}
				{radius}
				{strokeWidth}
			/>
		{/if}
	</svg>
</div>

<style>
	.energy-distribution-card {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: space-around;
	}

	@media screen and (min-width: 800px) {
		.energy-distribution-card {
			flex-direction: row;
		}
	}
</style>
